import * as React from 'react';

import { ActionType, NodeType, RaisedEventStatus } from '../../models';
import { createStructuredSelector, mconnect } from '../../state/redux';
import { IStyledComponent, styledComponent } from '../../theme';
import { IComponentStateProps, IConnectedComponentProps, INodeListStyles } from '../../types';
import { getNodeClassNames, getNodeStyles, getTextFieldComponentSpinnerStyles } from '../intentGraph.styles';
import {
  dispatchNodeRenderedEvent,
  dispatchFetchRelatedDialog,
  dispatchNodeResourcesFetch,
  dispatchUpdateInputFormFieldChanges
} from '../reducers/dispatchers';
import { updateRaisedEventAction } from '../reducers/actions';
import {
  contentIdsSelector,
  enableLinkClickLoggingSelector,
  getNodeSelector,
  globalVarsSelector,
  isDiagnosticActionInProgressSelector,
  isFetchingDialogInProgressSelector,
  isFetchingResourcesSelector,
  jsonPayloadMetaSelector,
  sessionIdSelector,
  skipRenderingTextFieldSelector
} from '../reducers/selectors.redux';

import { ChoiceNode } from './choiceNode';
import { DAFNode } from './dafNode';
import { SafActionNode } from './safActionNode';
import { SAFNode } from './safNode';
import { TextFieldComponent } from './textFieldComponent';
import { InputNode } from './inputNode';
import { Spinner } from 'office-ui-fabric-react';
import { PartnerNode } from './partnerSolutionNode';

type IProps = {
  renderStartNode?: boolean;
  nodeIndex?: number;
  uId?: string;
};

const mapStateToProps = createStructuredSelector({
  node: getNodeSelector,
  meta: jsonPayloadMetaSelector,
  sessionId: sessionIdSelector,
  isDiagnosticsActionInProgress: isDiagnosticActionInProgressSelector,
  globalVars: globalVarsSelector,
  enableLinkClickLogging: enableLinkClickLoggingSelector,
  isFetchingDialogInProgress: isFetchingDialogInProgressSelector,
  contentIds: contentIdsSelector,
  isFetchingResources: isFetchingResourcesSelector,
  skipRenderingTextField: skipRenderingTextFieldSelector
});

const mapDispatchToProps = {
  dispatchNodeRenderedEvent,
  updateRaisedEventAction,
  dispatchFetchRelatedDialog,
  dispatchNodeResourcesFetch,
  dispatchUpdateInputFormFieldChanges
};

type INodeProps = ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps &
  IStyledComponent<INodeListStyles> &
  IConnectedComponentProps &
  IComponentStateProps &
  IProps;

class NodeC extends React.PureComponent<INodeProps> {
  constructor(props: INodeProps) {
    super(props);
    if (window.yoda) {
      window.yoda.IntentGraph.Events.onRaisedEventCompleted = this.onRaisedEventCompleted;
    }
  }

  public componentDidMount = (): void => {
    if (
      !!this.props.node &&
      !!this.props.callbackHandlers.NodeRendered &&
      typeof this.props.callbackHandlers.NodeRendered === 'function'
    ) {
      this.props.dispatchNodeRenderedEvent(this.props.node, this.props.callbackHandlers.NodeRendered);
    }

    if (
      !!this.props.node &&
      !!this.props.node.get('type') &&
      this.props.node.get('type').toLowerCase() === NodeType.RedirectNode.toLowerCase()
    ) {
      this.props.dispatchFetchRelatedDialog(
        this.props.node,
        this.props.callbackHandlers.ContentRequestHandler,
        this.props.callbackHandlers.LogHandler,
        this.props.callbackHandlers.IsUserSignedIn
      );
    }

    if (this.props.contentIds && this.props.contentIds.length > 0) {
      if (
        this.props.callbackHandlers &&
        this.props.callbackHandlers.LogHandler &&
        this.props.callbackHandlers.ContentRequestHandler
      ) {
        this.props.dispatchNodeResourcesFetch(
          this.props.callbackHandlers.ContentRequestHandler,
          this.props.callbackHandlers.LogHandler,
          this.props.contentIds,
          this.props.node
        );
      }
    }

    if (
      !!this.props.node &&
      !this.props.isFetchingResources &&
      this.props.node.get('type').toLowerCase() === NodeType.InputNode.toLowerCase()
    ) {
      this.props.dispatchUpdateInputFormFieldChanges(this.props.node);
    }
  };

  public componentDidUpdate(prevProps: Readonly<INodeProps>): void {
    if (prevProps.isFetchingResources && !this.props.isFetchingResources) {
      if (
        !!this.props.node &&
        !this.props.isFetchingResources &&
        this.props.node.get('type').toLowerCase() === NodeType.InputNode.toLowerCase()
      ) {
        this.props.dispatchUpdateInputFormFieldChanges(this.props.node);
      }
    }
  }

  public render = (): React.ReactNode => {
    const classNames = getNodeClassNames(this.props.styles, {
      theme: this.props.theme,
      stylesFunc: this.props.stylesFunc
    });
    if (
      (this.props.node.get('type').toLowerCase() === NodeType.RedirectNode.toLowerCase() &&
        this.props.isFetchingDialogInProgress) ||
      this.props.isFetchingResources
    ) {
      return <Spinner styles={getTextFieldComponentSpinnerStyles(this.props)} />;
    }
    const nodefirstRender = this.props.node.get('isFirstRender');
    const isInEditMode = this.props.node.get('isInEditMode');
    const uId = this.props.node.get('uId');
    const dialogId = this.props.node.get('dialogId');
    return (
      <div key={this.props.node.get('uId')}>
        {this.props.node.get('skipNode') && (
          <TextFieldComponent
            componentkey={`${uId}$preConditionText`}
            key={`${uId}$preConditionText`}
            callbackHandlers={this.props.callbackHandlers}
            stylesFunc={this.props.stylesFunc}
            textContent={this.props.node.get('preCondition').get('text')}
            className={classNames.content}
            titleClassName={classNames.contentTitle}
            sessionId={this.props.sessionId}
            meta={this.props.meta}
            node={this.props.node}
            logLinkClicks={this.props.enableLinkClickLogging}
            globalVars={this.props.globalVars}
            dialogId={dialogId}
          />
        )}
        {this._renderTitle() && (
          <TextFieldComponent
            componentkey={`${uId}$title$${this.props.nodeIndex}`}
            key={`${uId}$title$${this.props.nodeIndex}`}
            callbackHandlers={this.props.callbackHandlers}
            stylesFunc={this.props.stylesFunc}
            textContent={this.props.node.get('title')}
            className={classNames.startNodeLabel}
            sessionId={this.props.sessionId}
            meta={this.props.meta}
            node={this.props.node}
            logLinkClicks={this.props.enableLinkClickLogging}
            globalVars={this.props.globalVars}
            dialogId={dialogId}
          />
        )}
        {this._renderText() && (
          <TextFieldComponent
            componentkey={`${uId}$text`}
            key={`${uId}$text`}
            callbackHandlers={this.props.callbackHandlers}
            stylesFunc={this.props.stylesFunc}
            textContent={this.props.node.get('text')}
            className={classNames.content}
            titleClassName={classNames.contentTitle}
            sessionId={this.props.sessionId}
            meta={this.props.meta}
            node={this.props.node}
            logLinkClicks={this.props.enableLinkClickLogging}
            globalVars={this.props.globalVars}
            dialogId={dialogId}
            ariaLive="assertive"
          />
        )}
        {this._renderQuestion() && (
          <div data-key={`graphNodeScrollTop$${this.props.nodeIndex}`}>
            <TextFieldComponent
              componentkey={`${uId}$question`}
              key={`${uId}$question`}
              callbackHandlers={this.props.callbackHandlers}
              stylesFunc={this.props.stylesFunc}
              textContent={this.props.node.get('question')}
              className={classNames.question}
              sessionId={this.props.sessionId}
              meta={this.props.meta}
              node={this.props.node}
              globalVars={this.props.globalVars}
              logLinkClicks={this.props.enableLinkClickLogging}
              dialogId={dialogId}
            />
          </div>
        )}
        {!this.props.node.get('skipNode') && this._renderNodeByType()}
        {!isInEditMode && !nodefirstRender && <hr className={classNames.hairline} />}
      </div>
    );
  };

  private onRaisedEventCompleted = (eventName: string, succeeded: boolean, uId: string): void => {
    const status = succeeded ? RaisedEventStatus.success : RaisedEventStatus.failure;
    this.props.updateRaisedEventAction(uId, eventName, status);
  };

  private _renderNodeByType = () => {
    const nodeId = this.props.node.get('id');
    const nodeType = this.props.node.get('type');
    const actionType = this.props.node.get('actionType');
    const uId = this.props.node.get('uId');
    const dialogId = this.props.node.get('dialogId');
    switch (nodeType.toLowerCase()) {
      case NodeType.ChoiceNode:
        return (
          <div key={uId}>
            <ChoiceNode
              nodeIndex={this.props.nodeIndex}
              componentkey={`${uId}$choiceNode`}
              key={uId}
              nodeId={nodeId}
              uId={uId}
              callbackHandlers={this.props.callbackHandlers}
              stylesFunc={this.props.stylesFunc}
              dialogId={dialogId}
            />
          </div>
        );
      case NodeType.DafDiagnosticNode:
        if (actionType && actionType.toLowerCase() === ActionType.SkylightSAF.toLowerCase()) {
          return (
            <SafActionNode
              componentkey={`${nodeId}$safActionDiagnosticsNode`}
              key={`${nodeId}$safActionDiagnosticsNode`}
              nodeId={nodeId}
              callbackHandlers={this.props.callbackHandlers}
              stylesFunc={this.props.stylesFunc}
              uId={uId}
              dialogId={dialogId}
            />
          );
        } else {
          return (
            <DAFNode
              componentkey={`${uId}$dafDiagnosticsNode`}
              key={`${uId}$dafDiagnosticsNode`}
              nodeId={nodeId}
              uId={uId}
              callbackHandlers={this.props.callbackHandlers}
              stylesFunc={this.props.stylesFunc}
              dialogId={dialogId}
            />
          );
        }
      case NodeType.SafDiagnosticNode:
        return (
          <SAFNode
            componentkey={`${uId}$safDiagnosticsNode`}
            key={`${uId}$safDiagnosticsNode`}
            nodeId={nodeId}
            uId={uId}
            callbackHandlers={this.props.callbackHandlers}
            stylesFunc={this.props.stylesFunc}
            dialogId={dialogId}
          />
        );
      case NodeType.InputNode:
        return (
          <InputNode
            componentkey={`${uId}$inputsNode`}
            key={`${uId}$inputNode`}
            nodeId={nodeId}
            uId={uId}
            callbackHandlers={this.props.callbackHandlers}
            stylesFunc={this.props.stylesFunc}
            dialogId={dialogId}
          />
        );
      case NodeType.PartnerSolutionNode:
        return (
          <PartnerNode
            componentkey={`${uId}$partnerNode`}
            key={`${uId}$partnerNode`}
            nodeId={nodeId}
            uId={uId}
            callbackHandlers={this.props.callbackHandlers}
            stylesFunc={this.props.stylesFunc}
            dialogId={dialogId}
          />
        );
    }
  };

  private _renderAccordion = (isFirstRender: boolean, nodeType: string) => {
    if (!isFirstRender) {
      return true;
    }
    if (nodeType === NodeType.TextNode) {
      return false;
    }
    return false;
  };

  private _renderTitle = () => {
    return (
      !this.props.skipRenderingTextField &&
      !this.props.node.get('skipNode') &&
      !this._renderAccordion(this.props.node.get('isFirstRender'), this.props.node.get('type').toLowerCase()) &&
      this.props.node.get('isFirstRender') &&
      this.props.node.get('type').toLowerCase() !== NodeType.DafDiagnosticNode &&
      !!this.props.node.get('title') &&
      this.props.node.get('title').size > 0 &&
      this.props.node.get('type').toLowerCase() !== NodeType.SafDiagnosticNode
    );
  };

  private _renderText = () => {
    const nodeType = this.props.node.get('type') ? this.props.node.get('type').toLowerCase() : '';
    return (
      !this.props.skipRenderingTextField &&
      !this.props.node.get('skipNode') &&
      !!this.props.node.get('text') &&
      this.props.node.get('text').size > 0 &&
      ((nodeType !== NodeType.DafDiagnosticNode && nodeType !== NodeType.SafDiagnosticNode) ||
        (nodeType === NodeType.DafDiagnosticNode && this.props.isDiagnosticsActionInProgress))
    );
  };

  private _renderQuestion = () => {
    return (
      !this.props.skipRenderingTextField &&
      !this.props.node.get('skipNode') && !!this.props.node.get('question') && this.props.node.get('question').size > 0
    );
  };
}

export const Node = mconnect(mapStateToProps, mapDispatchToProps)(styledComponent(NodeC, getNodeStyles));
